import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {Layout} from '../../components/layout';
import {Toast} from '../../components';
import {carService, printService} from '../../services';
import {useToast} from '../../hooks/useToast';
import {useSortPreference} from '../../hooks/useSortPreference';
import {useFilterPreference} from '../../hooks/useFilterPreference';
import {useSearchPreference} from '../../hooks/useSearchPreference';
import {Car, BulkPricingUpdatePayload, BulkLengthPricingPayload} from '../../types';
import {formatDateOrToday} from '../../utils/tableUtils';
import {FilterOption} from '../../components/tables';
import {CarsManagementView} from './components/CarsManagementView';

const PAGE_SIZE = 100;

interface TotalCarsPageProps {
    title?: string;
    isArchived?: boolean;
}

interface LocationState {
    selectedCarId?: string;
}

export const TotalCarsPage: React.FC<TotalCarsPageProps> = ({title = "Total Cars", isArchived = false}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LocationState | null;

    // Track the car to scroll to when returning from edit
    const [scrollToCarId, setScrollToCarId] = useState<string | undefined>(locationState?.selectedCarId);
    const [cars, setCars] = useState<Car[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [updatingCarIds, setUpdatingCarIds] = useState<Set<string>>(new Set());

    // Search preference persisted to localStorage
    const {searchQuery, searchColumn, setSearchQuery, setSearchColumn, clearSearch} = useSearchPreference({
        tableId: isArchived ? 'cars-table-archived' : 'cars-table-all',
        defaultColumn: 'chs_no',
    });

    // Sort preference persisted to localStorage
    const {sortColumn, sortDirection, handleSort: setSortPreference} = useSortPreference({
        tableId: isArchived ? 'cars-table-archived' : 'cars-table-all',
        defaultColumn: 'ref_no',
        defaultDirection: 'asc',
    });

    // Column filters for server-side filtering (persisted to localStorage)
    const {filters: columnFilters, setFilters: setColumnFilters, clearFilters} = useFilterPreference({
        tableId: isArchived ? 'cars-table-archived' : 'cars-table-all',
    });

    const {toast, showToast, hideToast} = useToast();
    const isLoadingRef = useRef(false);

    // Track previous isArchived value to detect actual changes (not initial mount)
    const prevIsArchivedRef = useRef<boolean | undefined>(undefined);
    // Track if we've completed the initial mount to prevent duplicate loads
    const initialMountCompleteRef = useRef(false);

    const loadCars = useCallback(async (reset = false) => {
        // Don't block if we're doing a reset (which means we want to force reload fresh data)
        if (isLoadingRef.current && !reset) return;

        try {
            isLoadingRef.current = true;
            setLoading(true);

            const skip = reset ? 0 : cars.length;
            const params: any = {skip, limit: PAGE_SIZE};

            if (searchColumn && searchQuery) {
                params.query = searchQuery;
                params.column = searchColumn;
            }

            if (sortColumn) {
                params.sort_column = sortColumn;
                params.sort_direction = sortDirection;
            }

            // Add column filters for server-side filtering
            if (columnFilters && Object.keys(columnFilters).length > 0) {
                params.filters = columnFilters;
            }

            // Use archived endpoint when in archived mode
            const carsData = isArchived
                ? await carService.getArchived(params)
                : await carService.getAllCarsWithRelations(params);

            if (reset) {
                setCars(carsData);
            } else {
                setCars(prev => [...prev, ...carsData]);
            }

            setHasMore(carsData.length === PAGE_SIZE);
        } catch (error: any) {
            console.error('Error loading cars:', error);
            let errorMessage = 'Failed to load cars';

            if (error?.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === 'string') {
                    errorMessage = detail;
                } else if (Array.isArray(detail)) {
                    errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
                }
            }

            showToast(errorMessage, 'error');
        } finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    }, [cars.length, searchColumn, searchQuery, sortColumn, sortDirection, columnFilters, showToast, isArchived]);

    // Clear location state after consuming it to prevent re-scroll on browser navigation
    useEffect(() => {
        if (locationState?.selectedCarId) {
            // Replace current history entry without the state to prevent re-scroll
            navigate(location.pathname, {replace: true, state: undefined});
            // Clear the scrollToCarId state after scroll completes (matches the 2s highlight duration in CarsTable)
            const timer = setTimeout(() => {
                setScrollToCarId(undefined);
            }, 2500);
            return () => clearTimeout(timer);
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset state and reload when isArchived changes (navigating between total/archived cars)
    useEffect(() => {
        const isInitialMount = prevIsArchivedRef.current === undefined;
        const isArchivedChanged = prevIsArchivedRef.current !== undefined && prevIsArchivedRef.current !== isArchived;
        prevIsArchivedRef.current = isArchived;

        // Check for success message from car entry page on mount
        if (isInitialMount) {
            const successMessage = sessionStorage.getItem('carEntrySuccess');
            if (successMessage) {
                sessionStorage.removeItem('carEntrySuccess');
                showToast(successMessage, 'success');
            }
        }

        if (isArchivedChanged) {
            // Only reset all state when actually navigating between archived/non-archived
            setCars([]);
            setHasMore(true);
            clearSearch();
            clearFilters();
            setScrollToCarId(undefined);
        }

        // Always load cars on mount or when isArchived changes
        if (isInitialMount || isArchivedChanged) {
            loadCars(true);
            // Mark initial mount as complete after loading
            if (isInitialMount) {
                initialMountCompleteRef.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isArchived]);

    // Reload when search/sort/filters change (but not on initial mount to avoid duplicate loads)
    useEffect(() => {
        if (initialMountCompleteRef.current) {
            loadCars(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, searchColumn, sortColumn, sortDirection, columnFilters]);

    const handleSort = (columnKey: string, newDirection: 'asc' | 'desc') => {
        setSortPreference(columnKey, newDirection);
    };

    const handleCarDoubleClick = (car: Car) => {
        if (!car.voyage_id) {
            showToast('Cannot edit car without voyage', 'warning');
            return;
        }
        navigate(`/car-entry/${car.voyage_id}/${car.id}`, {
            state: {
                from: {
                    pathname: isArchived ? '/archived-cars' : '/total-cars',
                    state: {selectedCarId: car.id}
                }
            }
        });
    };

    const handleToggleLoaded = async (car: Car, newValue: boolean) => {
        if (car.attachment_status) return;

        setUpdatingCarIds(prev => {
            const next = new Set(prev);
            next.add(car.id);
            return next;
        });

        try {
            const updatedCar = await carService.update(car.id, {loaded: newValue}) as Car;
            setCars(prev => prev.map(existingCar => (
                existingCar.id === car.id ? {...existingCar, ...updatedCar} : existingCar
            )));
        } catch (error: any) {
            console.error('Error updating loaded status:', error);
            let errorMessage = 'Failed to update loaded status';

            if (error?.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (typeof detail === 'string') {
                    errorMessage = detail;
                } else if (Array.isArray(detail)) {
                    errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
                }
            }

            showToast(errorMessage, 'error');
        } finally {
            setUpdatingCarIds(prev => {
                const next = new Set(prev);
                next.delete(car.id);
                return next;
            });
        }
    };

    // Bulk action handlers
    const handleBulkSetLoaded = async (carIds: string[], loaded: boolean) => {
        await carService.bulkSetLoaded(carIds, loaded);
        showToast(`Successfully marked ${carIds.length} car(s) as ${loaded ? 'loaded' : 'unloaded'}`, 'success');
        loadCars(true);
    };

    const handleChangeVoyageConfirm = async (carIds: string[], voyageId: string) => {
        const result = await carService.bulkUpdateVoyage(carIds, voyageId);
        const count = result?.updated_count ?? carIds.length;
        const skippedCount = result?.skipped_loaded?.length ?? 0;
        if (skippedCount > 0) {
            showToast(`Changed voyage for ${count} car(s). ${skippedCount} loaded car(s) were skipped.`, 'warning');
        } else {
            showToast(`Successfully changed voyage for ${count} car(s)`, 'success');
        }
        loadCars(true);
    };

    const handleChangeConsigneeConfirm = async (carIds: string[], consigneeId: string) => {
        await carService.bulkUpdateConsignee(carIds, consigneeId);
        showToast(`Updated consignee for ${carIds.length} car(s)`, 'success');
        loadCars(true);
    };

    const handleChangeDestinationConfirm = async (carIds: string[], portId: string) => {
        await carService.bulkUpdatePort(carIds, portId);
        showToast(`Successfully changed destination for ${carIds.length} car(s)`, 'success');
        loadCars(true);
    };

    const handleCombineBl = async (carIds: string[]) => {
        const result = await carService.combineBl(carIds);
        showToast(`Combined ${result.updated_count} units onto ${result.bl_number}`, 'success');
        loadCars(true);
    };

    const handleSeparateBl = async (carIds: string[]) => {
        const result = await carService.separateBl(carIds);
        const count = result?.updated_count ?? carIds.length;
        showToast(`Separated ${count} unit(s) from their BL`, 'success');
        loadCars(true);
    };

    const handleAddInsurance = async (carIds: string[]) => {
        const result = await carService.bulkAddInsurance(carIds);
        const count = result?.updated_count ?? carIds.length;
        showToast(`Added insurance to ${count} unit(s)`, 'success');
        loadCars(true);
    };

    const handleRemoveInsurance = async (carIds: string[]) => {
        const result = await carService.bulkRemoveInsurance(carIds);
        const count = result?.updated_count ?? 0;
        showToast(count === 0 ? 'None of the selected units had insurance' : `Removed insurance from ${count} unit(s)`, count === 0 ? 'info' : 'success');
        loadCars(true);
    };

    const handleSetPricingConfirm = async (carIds: string[], payload: BulkPricingUpdatePayload) => {
        await carService.bulkUpdatePricing(carIds, payload);
        showToast(`Successfully updated pricing for ${carIds.length} car(s)`, 'success');
        loadCars(true);
    };

    const handleSetLengthPricingConfirm = async (carIds: string[], payload: BulkLengthPricingPayload) => {
        await carService.bulkSetLengthPricing(carIds, payload);
        showToast(`Successfully updated pricing for ${carIds.length} car(s)`, 'success');
        loadCars(true);
    };

    const handleBulkDelete = async (carIds: string[]) => {
        await carService.bulkDelete(carIds);
        showToast(`Successfully deleted ${carIds.length} car(s)`, 'success');
        loadCars(true);
    };

    const handleDeleteCar = async (car: Car) => {
        await carService.bulkDelete([car.id]);
        showToast('Car deleted successfully', 'success');
        loadCars(true);
    };

    // Get filter options from backend for a specific column (dynamic based on other active filters)
    const getFilterOptions = useCallback(async (columnKey: string, currentFilters?: Record<string, string[]>): Promise<FilterOption[]> => {
        try {
            return await carService.getFilterOptions(columnKey, currentFilters, isArchived ? {is_archived: true} : undefined);
        } catch (error) {
            console.error('Error fetching filter options:', error);
            return [];
        }
    }, [isArchived]);

    const handlePrintBookingConfirmation = async (selectedCars: Car[], options: { includeRelated: boolean } = { includeRelated: true }) => {
        if (selectedCars.length === 0) {
            showToast('No cars selected', 'warning');
            return;
        }

        try {
            const firstCar = selectedCars[0];
            const bookingNumber = firstCar?.booking_reference || '';
            const originalBooking = firstCar?.original_booking;

            const polTerminal = originalBooking?.port_terminal || '';
            const polAddress = originalBooking?.port_address || '';
            const polName = originalBooking?.port_name || '';
            const polRemarks = originalBooking?.port_remarks || '';
            const pol = polName;

            // Build the 9-row car entries with proper structure matching BookingConfirmationPage
            const carEntries: Array<{
                nr: number;
                description: string;
                vinSerial: string;
                destination: string;
                relatedCars?: Array<{
                    relationshipType: string;
                    makeModel: string;
                    vinSerial: string;
                }>;
            }> = [];

            let totalUnits = 0;
            for (const car of selectedCars) {
                if (carEntries.length >= 9) break;

                const dbMake = car.make?.name || '';
                const dbModel = car.model?.name || '';
                // Use database make/model if available, otherwise use manual_make_model
                const makeModel = dbMake && dbModel
                    ? `${dbMake} ${dbModel}`
                    : (dbMake || dbModel || car.manual_make_model || '');

                // Build related cars array if includeRelated is true
                let relatedCarsInfo: Array<{
                    relationshipType: string;
                    makeModel: string;
                    vinSerial: string;
                }> | undefined;

                if (options.includeRelated && car.related_cars && car.related_cars.length > 0) {
                    // Sort: Attached first, then STC
                    const sortedRelatedCars = [...car.related_cars].sort((a, b) => {
                        if (a.relationship_type === 'Attached' && b.relationship_type !== 'Attached') return -1;
                        if (a.relationship_type !== 'Attached' && b.relationship_type === 'Attached') return 1;
                        return 0;
                    });

                    relatedCarsInfo = sortedRelatedCars.map(rc => {
                        const rcDbMake = rc.make?.name || '';
                        const rcDbModel = rc.model?.name || '';
                        // Use database make/model if available, otherwise use manual_make_model
                        const rcMakeModel = rcDbMake && rcDbModel
                            ? `${rcDbMake} ${rcDbModel}`
                            : (rcDbMake || rcDbModel || rc.manual_make_model || '-');
                        return {
                            relationshipType: rc.relationship_type || 'STC',
                            makeModel: rcMakeModel.trim() || '-',
                            vinSerial: rc.chassis_number
                        };
                    });
                    totalUnits += relatedCarsInfo.length;
                }

                carEntries.push({
                    nr: carEntries.length + 1,
                    description: makeModel.trim() || '-',
                    vinSerial: car.chassis_number,
                    destination: car.port?.port || car.port?.code || '',
                    relatedCars: relatedCarsInfo
                });
                totalUnits++;
            }

            // Fill remaining slots with empty entries
            while (carEntries.length < 9) {
                carEntries.push({
                    nr: carEntries.length + 1,
                    description: '',
                    vinSerial: '',
                    destination: ''
                });
            }

            const filename = `Booking Confirmation ${pol || 'Unknown'} ${bookingNumber || 'N/A'}.pdf`;

            await printService.generateBookingConfirmation({
                bookingNumber: bookingNumber || undefined,
                terminal: polTerminal,
                date: formatDateOrToday(firstCar?.created_at),
                pol: pol,
                terminalAddress: polAddress,
                cars: carEntries,
                portRemarks: polRemarks || undefined
            }, filename);

            showToast(`Booking Confirmation generated for ${totalUnits} unit${totalUnits !== 1 ? 's' : ''}`, 'success');
        } catch (error) {
            console.error('Error generating booking confirmation:', error);
            showToast('Failed to generate booking confirmation', 'error');
        }
    };

    // Handle backend search
    const handleSearch = useCallback((query: string, column: string) => {
        setSearchQuery(query);
        setSearchColumn(column);
    }, [setSearchQuery, setSearchColumn]);

    // Handle filter changes from CarsManagementView
    const handleFiltersChange = useCallback((filters: Record<string, string[]>) => {
        setColumnFilters(filters);
    }, [setColumnFilters]);

    const totalMainCars = cars.filter(car => !car.attachment_status).length;

    // Create header content for CarsManagementView
    const headerContent = useMemo(() => (
        <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
                {totalMainCars} {totalMainCars === 1 ? 'record' : 'records'} loaded
            </p>
        </div>
    ), [title, totalMainCars]);

    return (
        <Layout fullWidth>
            <CarsManagementView
                headerContent={headerContent}
                cars={cars}
                loading={loading}
                showVoyageColumn={true}
                focusMode="global"
                tableId={isArchived ? 'cars-table-archived' : 'cars-table-all'}
                getFilterOptions={getFilterOptions}
                onFiltersChange={handleFiltersChange}
                columnFilters={columnFilters}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                hasMore={hasMore}
                onLoadMore={() => loadCars(false)}
                scrollToCarId={scrollToCarId}
                onRowDoubleClick={handleCarDoubleClick}
                onEditCar={handleCarDoubleClick}
                onToggleLoaded={handleToggleLoaded}
                loadingLoadedIds={Array.from(updatingCarIds)}
                onBulkChangeVoyage={isArchived ? undefined : handleChangeVoyageConfirm}
                onBulkChangeConsignee={isArchived ? undefined : handleChangeConsigneeConfirm}
                onBulkChangeDestination={isArchived ? undefined : handleChangeDestinationConfirm}
                onBulkUpdatePricing={isArchived ? undefined : handleSetPricingConfirm}
                onBulkSetLengthPricing={isArchived ? undefined : handleSetLengthPricingConfirm}
                onBulkSetLoaded={isArchived ? undefined : handleBulkSetLoaded}
                onBulkCombineBl={isArchived ? undefined : handleCombineBl}
                onBulkSeparateBl={isArchived ? undefined : handleSeparateBl}
                onBulkAddInsurance={isArchived ? undefined : handleAddInsurance}
                onBulkRemoveInsurance={isArchived ? undefined : handleRemoveInsurance}
                onBulkDelete={isArchived ? undefined : handleBulkDelete}
                onDeleteCar={isArchived ? undefined : handleDeleteCar}
                onPrintBookingConfirmation={handlePrintBookingConfirmation}
                onRefresh={() => loadCars(true)}
                onSearch={handleSearch}
            />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={hideToast}
                />
            )}
        </Layout>
    );
};

export default TotalCarsPage;
